// src/components/store/sections/testimonials/DynamicTestimonialVariant.tsx
"use client";

import React, { useRef } from "react";
import Image from "next/image";
import { Star, ChevronLeft, ChevronRight, Quote, ShieldCheck } from "lucide-react";

const FALLBACK_REVIEWS = [
  {
    id: 1,
    customer_name: "Nusrat Jahan",
    designation: "Verified Buyer • Dhaka",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=200&q=80",
    comment: "Packaging and product finishing were top-notch! Delivered within 24 hours and matched the photos 100%.",
  },
  {
    id: 2,
    customer_name: "Mehedi Hasan",
    designation: "Regular Shopper • Khulna",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80",
    comment: "খুবই চমৎকার কোয়ালিটি এবং সার্ভিস। অনলাইনে অর্ডার করার পর এত দ্রুত ডেলিভারি পাবো ভাবিনি!",
  },
  {
    id: 3,
    customer_name: "Sadia Afrin",
    designation: "Fashion Enthusiast • Chattogram",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=80",
    comment: "Their customer support and quality assurance are unmatched. Every item feels premium and authentic.",
  },
  {
    id: 4,
    customer_name: "Rafiqul Islam",
    designation: "Verified Customer • Sylhet",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=80",
    comment: "Best online shopping experience this year. Highly recommended for authentic products!",
  },
];

export default function DynamicTestimonialVariant({
  variantNumber,
  archKey,
  archLabel,
  palName,
  grad,
  lightBg,
  textCol,
  borderCol,
  testimonials = [],
}: any) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const items = Array.isArray(testimonials) && testimonials.length > 0 ? testimonials : FALLBACK_REVIEWS;
  const isDark = archKey === "cyber_verified" || archKey === "podium_stars" || palName === "SlateGold";

  const scroll = (dir: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === "left" ? -320 : 320, behavior: "smooth" });
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 select-none font-sans space-y-6">
      <div className="flex items-center justify-between border-b border-slate-200/80 pb-4">
        <div>
          <span className={`text-xs font-black uppercase tracking-wider flex items-center gap-1.5 font-mono ${textCol}`}>
            <Quote className="w-3.5 h-3.5" /> Variant {variantNumber}: {archLabel} ({palName})
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight mt-0.5">
            Customer Love & Verified Reviews
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => scroll("left")}
            className={`w-8 h-8 rounded-full border-2 ${borderCol} bg-white text-slate-700 flex items-center justify-center shadow-xs cursor-pointer active:scale-95`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => scroll("right")}
            className={`w-8 h-8 rounded-full border-2 ${borderCol} bg-white text-slate-700 flex items-center justify-center shadow-xs cursor-pointer active:scale-95`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex items-stretch gap-5 overflow-x-auto pb-4 pt-2 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden scroll-smooth"
      >
        {items.map((rev: any, idx: number) => {
          const av = rev.avatar && rev.avatar.startsWith("http") ? rev.avatar : FALLBACK_REVIEWS[idx % FALLBACK_REVIEWS.length].avatar;
          return (
            <div
              key={rev.id || idx}
              className={`w-72 sm:w-80 shrink-0 ${
                archKey === "arch_portraits" ? "rounded-t-[65px] rounded-b-3xl" : "rounded-3xl"
              } ${
                isDark ? "bg-slate-950 text-white border-slate-800" : `bg-white text-slate-900 ${borderCol}`
              } border-2 p-5 shadow-md hover:shadow-xl transition-all flex flex-col justify-between gap-4`}
            >
              <div className="space-y-3 text-left">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-0.5 text-amber-400">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className="w-3.5 h-3.5 fill-amber-400" />
                    ))}
                  </div>
                  <span className={`px-2.5 py-0.5 rounded-full bg-gradient-to-r ${grad} text-white text-[9px] font-black uppercase flex items-center gap-1`}>
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </span>
                </div>
                <p className={`text-xs sm:text-sm leading-relaxed italic ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  "{rev.comment}"
                </p>
              </div>

              <div className="flex items-center gap-3 pt-3 border-t border-slate-100/80">
                <div className={`relative w-11 h-11 rounded-full overflow-hidden border-2 ${borderCol} shrink-0`}>
                  <Image src={av} alt={rev.customer_name || "Customer"} fill unoptimized className="object-cover" />
                </div>
                <div className="text-left min-w-0">
                  <h4 className="text-xs sm:text-sm font-black truncate">{rev.customer_name || "Verified Shopper"}</h4>
                  <span className={`text-[10px] font-bold block truncate ${textCol}`}>{rev.designation || "Verified Buyer"}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
